/**
 * Socle applicatif partage entre le client et le rendu serveur.
 *
 * App.tsx enveloppe ces pieces dans un BrowserRouter, entry-server.tsx dans un
 * StaticRouter. Tout ce qui touche au DOM au moment du rendu vit derriere
 * <ClientOnly>, sinon le pre-rendu Node planterait.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { FondAnime } from "@/components/decor/FondAnime";
import { Choregraphie } from "@/components/motion/Choregraphie";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/components/ThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Services from "./pages/Services";
import { BlogList } from "./pages/blog/BlogList";
import { BlogPost } from "./pages/blog/BlogPost";
import ProjectsIndex from "./pages/projects/ProjectsIndex";
import ProjectDetail from "./pages/projects/ProjectDetail";
import AiguilleurServices from "./pages/services/AiguilleurServices";
import PortfolioAbout from "./components/portfolio/PortfolioAbout";
import PortfolioContactPage from "./components/portfolio/PortfolioContactPage";

/** Un client par rendu : sur le serveur, deux pages ne doivent jamais partager un cache. */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        retry: 1,
      },
    },
  });
}

/**
 * Ne rend ses enfants qu'apres le montage cote navigateur.
 * Le premier rendu client est donc identique au HTML du serveur, ce qui evite
 * les avertissements d'hydratation sur les composants qui lisent le DOM.
 */
export function ClientOnly({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? <>{children}</> : null;
}

export function AppRoutes() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const editorialPage = ["/", "/services", "/projets", "/a-propos", "/contact"].includes(pathname.replace(/\/$/, "") || "/") || /^\/blog(?:\/[^/]+)?\/?$/.test(pathname);

  useEffect(() => {
    // Les anciennes ancres restent utilisables depuis les fiches et les liens partagés.
    const legacyPages: Record<string, string> = { "#a-propos": "/a-propos", "#approche": "/services", "#contact": "/contact", "#projets": "/projets" };
    if (pathname === "/" && legacyPages[hash]) {
      navigate(legacyPages[hash], { replace: true });
      return;
    }
    const frame = requestAnimationFrame(() => {
      if (hash) {
        try { document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView(); } catch { /* Un fragment invalide ne bloque pas la page. */ }
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        document.getElementById("portfolio-content")?.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash, navigate]);
  return (
    <>
      {!editorialPage && <FondAnime />}
      {/* Un seul observateur pour toutes les entrees au defilement. Il vit
          ici et pas dans AppProviders : useLocation exige d'etre sous le
          routeur, et le routeur est monte a l'interieur des providers. */}
      <Choregraphie />
<Routes>
        <Route path="/" element={<Index />} />
        <Route path="/services" element={<Services />} />
        <Route path="/a-propos" element={<PortfolioAbout />} />
        <Route path="/contact" element={<PortfolioContactPage />} />
        {/* Un seul segment dynamique sous /services, partage par les pages
            villes et les pages services. React Router 6 n'accepte un parametre
            que sur un segment entier et ne sait pas distinguer deux routes de
            meme forme : l'arbitrage sur le slug vit donc dans l'aiguilleur.
            Une future route statique sous /services passera devant celle-ci :
            le classement de v6 prime le segment fixe. */}
        <Route path="/services/:page" element={<AiguilleurServices />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/projets" element={<ProjectsIndex />} />
        <Route path="/projets/:slug" element={<ProjectDetail />} />
        {/* AJOUTER TOUTE NOUVELLE ROUTE AU-DESSUS DE LA ROUTE ATTRAPE-TOUT "*" */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export function AppProviders({
  client,
  helmetContext,
  children,
}: {
  client: QueryClient;
  helmetContext?: Record<string, unknown>;
  children: ReactNode;
}) {
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <HelmetProvider context={helmetContext}>
          <TooltipProvider>
            <ClientOnly>
              <Toaster />
              <Sonner />
            </ClientOnly>
            {children}
          </TooltipProvider>
        </HelmetProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
